import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Copy, Ban, Link2, CheckCircle2, Send, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { VOLUNTEER_ROLES, VOLUNTEER_ROLE_LABELS } from "@/lib/roles";

const selectCls = "w-full rounded-xl border border-[#e4d8c4] bg-white px-3 py-2.5 text-sm text-[#00334a]";

export default function InvitesTab() {
  const [campaigns, setCampaigns] = useState([]);
  const [teams, setTeams] = useState([]);
  const [invites, setInvites] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState("supporter"); // supporter | volunteer
  const [form, setForm] = useState({ campaign_id: "", team_id: "", email: "", note: "", volunteer_role: "brand_rep" });

  useEffect(() => { api.get("/campaigns").then(({ data }) => setCampaigns(data)); }, []);
  const load = useCallback(() => { api.get("/admin/invites").then(({ data }) => setInvites(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (form.campaign_id) api.get(`/teams?campaign_id=${form.campaign_id}`).then(({ data }) => setTeams(data));
    else setTeams([]);
  }, [form.campaign_id]);

  const openNew = () => { setKind("supporter"); setForm({ campaign_id: campaigns[0]?.id || "", team_id: "", email: "", note: "", volunteer_role: "brand_rep" }); setOpen(true); };

  const inviteUrl = (t) => `${window.location.origin}/join/${t}`;
  const copy = (t) => { navigator.clipboard.writeText(inviteUrl(t)); toast.success("Invite link copied!"); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = kind === "volunteer"
        ? { email: form.email || null, note: form.note, volunteer_role: form.volunteer_role }
        : { campaign_id: form.campaign_id, team_id: form.team_id || null, email: form.email || null, note: form.note };
      await api.post("/admin/invites", payload);
      toast.success(form.email ? "Invite created & emailed" : "Invite link created");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const revoke = async (id) => { if (!window.confirm("Revoke this invite?")) return; await api.post(`/admin/invites/${id}/revoke`); toast.success("Invite revoked"); load(); };
  const resend = async (id) => { try { await api.post(`/admin/invites/${id}/resend`); toast.success("Invite re-sent"); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const del = async (id) => { if (!window.confirm("Delete this invite permanently?")) return; await api.delete(`/admin/invites/${id}`); toast.success("Invite deleted"); load(); };

  const status = (inv) => {
    if (inv.used) return { t: "Used", c: "bg-[#e8e7d6] text-[#5f6a48]" };
    if (inv.revoked) return { t: "Revoked", c: "bg-[#f3e7db] text-[#b05e3c]" };
    return { t: "Active", c: "bg-[#efe6d6] text-[#3f6670]" };
  };

  return (
    <div className="space-y-6" data-testid="admin-invites">
      <div className="flex items-center justify-between">
        <div>
          <span className="hf-eyebrow">Invite-only access</span>
          <h1 className="mt-1 font-heading text-3xl font-semibold text-[#00334a]">Invites</h1>
          <p className="mt-1 text-sm text-[#7c7a6a]">Invite supporters to fundraise or volunteers to join the team.</p>
        </div>
        <Button onClick={openNew} className="rounded-full hf-btn-coral font-bold" data-testid="admin-create-invite-button">
          <Plus className="mr-2 h-4 w-4" /> New invite
        </Button>
      </div>

      {invites.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-[#e4d8c4] bg-white p-12 text-center">
          <Link2 className="mx-auto h-10 w-10 text-[#e4d8c4]" />
          <p className="mt-3 font-semibold text-[#00334a]">No invites yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {invites.map((inv) => {
            const s = status(inv);
            return (
              <div key={inv.id} className="flex flex-wrap items-center gap-3 rounded-2xl border border-[#e4d8c4] bg-white p-4" data-testid={`invite-row-${inv.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-[#00334a]">{inv.email || "Open link"}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${s.c}`}>{s.t}</span>
                    <span className="rounded-full bg-[#efe9dd] px-2 py-0.5 text-[10px] font-bold uppercase text-[#5a5849]">{inv.kind || "supporter"}</span>
                  </div>
                  <div className="text-xs text-[#7c7a6a]">
                    {inv.volunteer_role ? inv.volunteer_role_label : (inv.campaign_name || "—")}{inv.team_name ? ` · ${inv.team_name}` : ""}
                    {inv.used && inv.used_by_name ? ` · used by ${inv.used_by_name}` : ""}
                  </div>
                </div>
                {!inv.used && !inv.revoked && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => copy(inv.token)} className="rounded-full border-[#e4d8c4] text-[#3f6670]" data-testid={`invite-copy-${inv.id}`}><Copy className="mr-1.5 h-3.5 w-3.5" /> Copy</Button>
                    {inv.email && <Button size="sm" variant="outline" onClick={() => resend(inv.id)} className="rounded-full border-[#e4d8c4] text-[#3f6670]" data-testid={`invite-resend-${inv.id}`}><Send className="mr-1.5 h-3.5 w-3.5" /> Resend</Button>}
                    <Button size="sm" variant="ghost" onClick={() => revoke(inv.id)} className="rounded-full text-[#c97049]" data-testid={`invite-revoke-${inv.id}`}><Ban className="mr-1.5 h-3.5 w-3.5" /> Revoke</Button>
                  </>
                )}
                {inv.used && <CheckCircle2 className="h-5 w-5 text-[#87856c]" />}
                <Button size="icon" variant="ghost" onClick={() => del(inv.id)} className="h-8 w-8 text-[#c97049]" data-testid={`invite-delete-${inv.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="invite-dialog">
          <DialogHeader><DialogTitle className="font-heading">Create invite</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setKind("supporter")} className={`rounded-xl border py-2.5 text-sm font-semibold ${kind === "supporter" ? "border-[#65949c] bg-[#efe6d6] text-[#00334a]" : "border-[#e4d8c4] text-[#7c7a6a]"}`} data-testid="invite-kind-supporter">Supporter</button>
              <button type="button" onClick={() => setKind("volunteer")} className={`rounded-xl border py-2.5 text-sm font-semibold ${kind === "volunteer" ? "border-[#65949c] bg-[#efe6d6] text-[#00334a]" : "border-[#e4d8c4] text-[#7c7a6a]"}`} data-testid="invite-kind-volunteer">Volunteer</button>
            </div>

            {kind === "supporter" ? (
              <>
                <div className="space-y-1.5">
                  <Label>Campaign</Label>
                  <select value={form.campaign_id} onChange={(e) => setForm({ ...form, campaign_id: e.target.value, team_id: "" })} className={selectCls} required data-testid="invite-campaign-select">
                    <option value="">Select campaign…</option>
                    {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label>Assign to team (optional)</Label>
                  <select value={form.team_id} onChange={(e) => setForm({ ...form, team_id: e.target.value })} className={selectCls} data-testid="invite-team-select">
                    <option value="">Let them choose / create</option>
                    {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
              </>
            ) : (
              <div className="space-y-1.5">
                <Label>Volunteer role</Label>
                <select value={form.volunteer_role} onChange={(e) => setForm({ ...form, volunteer_role: e.target.value })} className={selectCls} data-testid="invite-volunteer-role-select">
                  {VOLUNTEER_ROLES.map((r) => <option key={r} value={r}>{VOLUNTEER_ROLE_LABELS[r]}</option>)}
                </select>
              </div>
            )}

            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required placeholder="person@email.com" data-testid="invite-email-input" />
              <p className="text-xs text-[#7c7a6a]">We'll email them the invite link automatically.</p>
            </div>
            <div className="space-y-1.5"><Label>Note (internal)</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} /></div>
            <DialogFooter><Button type="submit" disabled={busy} className="rounded-full hf-btn-coral font-bold" data-testid="invite-save-button">{busy ? "Creating…" : "Create & send invite"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
